import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { money } from "@/dealership/lib/api";
import { makeText, P3_CHECK_GROUPS, toggleCheck, REMARKS_MAX, DETAILS_MAX } from "@/dealership/lib/billOfSaleText";

/** A single letter-sized page. Module-level so it is a stable component type
 * (defining it inside BillOfSaleDocument would remount every page — and any
 * overlay refs — on each parent render). */
function BosPage({ n, only, overlay, className = "", style, children }) {
  if (only != null && only !== n) return null;
  return (
    <div
      className={`bos-page print-area max-w-[8.5in] mx-auto my-6 bg-white shadow-sm border border-zinc-300 p-8 text-zinc-900 relative ${className}`}
      style={style}
      data-bos-page={n}
      data-testid={`bill-of-sale-page-${n}`}
    >
      {children}
      {/* Page number — pinned to the bottom-right corner of every page so it sits
          in the same place on all four (TICKET-DEAL-001). */}
      <div className="absolute right-8 bottom-6 text-[9px] text-zinc-400 pointer-events-none" data-testid={`bos-page-number-${n}`}>
        {n} / 4
      </div>
      {overlay && (
        <div className="absolute inset-0 pointer-events-none z-20" data-bos-overlay={n}>
          {overlay(n)}
        </div>
      )}
    </div>
  );
}


/**
 * Shared 4-page Bill of Sale document. Used both by the live Bill of Sale
 * (with fillable signing spots) and by the Settings placement editor (with
 * draggable markers). `overlay(pageNum)` renders an absolute layer on top of
 * each page — the parent page div is `relative` so spots position by %.
 */
export function BillOfSaleDocument({
  d, dealer, overlay, only, texts,
  p3Mode = "new", editable = false,
  fiShow = true, fiName = "", dealerFiDefault = "",
  onToggleCheck, onCommitNotes, onCommitDetails,
  onSetServicePlan, onToggleFi, onCommitFiName,
}) {
  const t = d.totals || {};
  const bal = d.balance || {};
  const T = makeText(texts, d, dealer);
  const checks = d.bos_checks || {};

  const addOns = [
    { label: d.warranty_name ? `Warranty — ${d.warranty_name}` : "Warranty", amount: d.warranty_price, show: d.warranty_price > 0 },
    { label: "Safety / Certification", amount: d.safety_cert_fee, show: d.safety_cert_fee > 0 },
    { label: "Administration Fee", amount: d.admin_fee, show: d.admin_fee > 0 },
    ...(d.other_fees || []).map((f) => ({ label: f.label + (f.taxable === false ? " (tax-exempt)" : ""), amount: f.amount, show: f.amount > 0 })),
  ].filter((x) => x.show);

  const vehFields = [
    ["YEAR", d.vehicle_year || ""],
    ["TYPE", d.vehicle_type || ""],
    ["MAKE", d.vehicle_make || ""],
    ["MODEL", d.vehicle_model || ""],
    ["TRIM", d.vehicle_trim || ""],
    ["PKG", d.vehicle_pkg || ""],
    ["EXT. COLOR", d.vehicle_color || ""],
    ["INT. COLOR", d.vehicle_int_color || ""],
    ["VIN", d.vehicle_vin || ""],
    ["TOTAL DIST. TRAV.", d.vehicle_odometer ? `${Number(d.vehicle_odometer).toLocaleString()} km` : ""],
    ["DELIVERY DATE", d.deal_date || ""],
  ];

  const hasFinance = d.finance_company || d.finance_amount > 0 || d.finance_payment > 0 || d.finance_apr;
  const dealDate = d.deal_date || (d.created_at || "").slice(0, 10);

  return (
    <>
      {/* ============ PAGE 1 ============ */}
      <BosPage only={only} overlay={overlay} n={1} className="text-[11px] leading-snug" >
        <div className="flex justify-between text-[10px] font-mono-data">
          <span>HST# {dealer.hst_number}</span>
          <span>STOCK# {d.vehicle_stock || d.bill_number}</span>
        </div>
        <div className="text-center mt-1">
          <h1 className="font-heading text-lg font-extrabold uppercase tracking-wide">{T("doc_title")}</h1>
          <p className="font-bold mt-1">{dealer.name}</p>
          <p className="text-zinc-600">{dealer.address}</p>
        </div>
        <div className="flex justify-between text-[10px] mt-1">
          <span>DEALER REG# {dealer.reg}</span>
          <FiManagerLine
            editable={editable}
            show={fiShow}
            name={fiName}
            dealerDefault={dealerFiDefault}
            onToggle={onToggleFi}
            onCommitName={onCommitFiName}
          />
        </div>

        <SectionTitle>{T("sec_buyer")}</SectionTitle>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 border border-zinc-300 p-2">
          <div className="space-y-1">
            <Field label="NAME" value={d.customer_name} />
            <Field label="ADDRESS" value={d.customer_address} />
            <Field label="CITY / PROV / POSTAL" value={[d.customer_city, d.customer_province, d.customer_postal].filter(Boolean).join(", ")} />
            <Field label="PHONE" value={d.customer_phone} />
            <Field label="EMAIL" value={d.customer_email} />
            <Field label="DRIVER'S LIC." value={d.customer_license} />
          </div>
          <div className="space-y-1">
            <Field label="DATE OF SALE" value={dealDate} />
            <Field label="INSURANCE" value={d.insurance_company} />
            <Field label="POLICY NO." value={d.insurance_policy} />
            <Field label="EXP. DATE" value={d.insurance_exp} />
          </div>
        </div>

        <SectionTitle>{T("sec_vehicle")}</SectionTitle>
        <div className="grid grid-cols-3 gap-x-6 gap-y-1 border border-zinc-300 p-2">
          {vehFields.map(([l, v]) => <Field key={l} label={l} value={v} />)}
        </div>

        <p className="mt-2 text-[10.5px]">{T("purchaser_ack")}</p>

        <div className="grid grid-cols-2 gap-6 mt-2">
          <div>
            <SectionTitle>{T("sec_price")}</SectionTitle>
            <div className="border border-zinc-300 p-3 font-mono-data text-[11px]">
              <PriceLine l="Retail Price" v={money(d.sale_price)} />
              <PriceLine l="Total Vehicle Price" v={money(d.sale_price)} />
              {addOns.map((a, i) => <PriceLine key={i} l={a.label} v={money(a.amount)} />)}
              {t.trade_in_value > 0 && <PriceLine l={`Trade-In${d.trade_in_desc ? ` (${d.trade_in_desc})` : ""}`} v={`- ${money(t.trade_in_value)}`} />}
              <div className="border-t border-zinc-300 my-1" />
              <PriceLine l="Subtotal" v={money(t.taxable_net)} />
              {t.tax_exempt
                ? <PriceLine l="HST" v="TAX EXEMPT" />
                : <PriceLine l="HST (13%)" v={money(t.hst)} />}
              {t.non_taxable_fees > 0 && <PriceLine l="Tax-Exempt Fees" v={money(t.non_taxable_fees)} />}
              {t.licensing_fee > 0 && <PriceLine l="Licensing / PPSA" v={money(t.licensing_fee)} />}
              {d.lien_amount > 0 && <PriceLine l="Lien Payout" v={money(d.lien_amount)} />}
              <div className="border-t-2 border-zinc-900 my-1" />
              <PriceLine l="TOTAL" v={money(t.total)} bold />
              {bal.financed_credit_applied > 0 && <PriceLine l="Less Amount Financed (Funded)" v={`- ${money(bal.financed_credit_applied)}`} />}
              {bal.total_paid > 0 && <PriceLine l="Total Paid" v={`- ${money(bal.total_paid)}`} />}
              <PriceLine l="Balance Due" v={money(bal.balance_due != null ? bal.balance_due : t.total)} bold />
            </div>
          </div>

          <div>
            <SectionTitle>{T("sec_disclosures")}</SectionTitle>
            <div className="border border-zinc-300 p-3 space-y-2 text-[9.5px] leading-relaxed">
              {[
                ["disc_privacy_title", "disc_privacy_body"],
                ["disc_incentive_title", "disc_incentive_body"],
                ["disc_financing_title", "disc_financing_body"],
                ["disc_mvda_title", "disc_mvda_body"],
              ].map(([tk, bk]) => <Disclosure key={tk} title={T(tk)}>{T(bk)}</Disclosure>)}
            </div>
          </div>
        </div>

        <div className="border border-zinc-900 mt-2 p-2">
          <p className="text-center font-bold uppercase text-[11px]">{T("sales_final_title")}</p>
          <p className="text-[9.5px] mt-1">{T("sales_final_body")}</p>
          <div className="flex justify-between font-mono-data font-bold text-[12px] mt-1">
            <span>TOTAL</span><span>{money(t.total)}</span>
          </div>
        </div>

        {hasFinance && (
          <>
            <SectionTitle>{T("sec_finance")}</SectionTitle>
            <div className="grid grid-cols-4 gap-x-6 gap-y-1 border border-zinc-300 p-2">
              <Field label="LENDER" value={d.finance_company} />
              <Field label="AMOUNT FIN." value={d.finance_amount ? money(d.finance_amount) : ""} />
              <Field label="APR" value={d.finance_apr ? `${d.finance_apr}%` : ""} />
              <Field label="TERM" value={d.finance_term} />
              <Field label="FREQUENCY" value={d.finance_frequency} />
              <Field label="PAYMENT" value={d.finance_payment ? money(d.finance_payment) : ""} />
              <Field label="FIRST PMT" value={d.finance_first_payment} />
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-6 mt-2">
          <div>
            <SectionTitle>{T("sec_service_plan")}</SectionTitle>
            <ServicePlanChoice
              prompt={T("service_plan_prompt")}
              value={d.service_plan || ""}
              editable={editable}
              onSet={onSetServicePlan}
            />
          </div>
        </div>
        <div className="mt-2">
          <SectionTitle>{T("sec_remarks")}</SectionTitle>
          <EditableNoteBox
            value={d.notes || ""}
            editable={editable}
            onCommit={onCommitNotes}
            maxLength={REMARKS_MAX}
            heightClass="h-[0.8in]"
            placeholder="Remarks…"
            testid="p1-remarks"
          />
        </div>

        {/* signature area — spots are placed over this whitespace via the overlay */}
        <div className="h-24 mt-6" />
      </BosPage>

      {/* ============ PAGE 2 ============ */}
      <BosPage only={only} overlay={overlay} n={2} className="text-[10px] leading-relaxed" style={{ breakBefore: "page" }}>
        <h2 className="font-heading font-bold text-sm mb-2">{T("p2_safety_title")}</h2>
        <p>{T("p2_safety_body")}</p>
        <h2 className="font-heading font-bold text-sm mt-4 mb-2">{T("p2_asis_title")}</h2>
        <p>{T("p2_asis_body")}</p>
        <h2 className="font-heading font-bold text-sm mt-4 mb-2">{T("p2_omvic_title")}</h2>
        <p>{T("p2_omvic_body")}</p>
        <p className="mt-1">{T("p2_omvic_contact")}</p>
        <h2 className="font-heading font-bold text-sm mt-4 mb-2">{T("p2_camvap_title")}</h2>
        <p>{T("p2_camvap_body")}</p>
      </BosPage>

      {/* ============ PAGE 3 ============ */}
      <BosPage only={only} overlay={overlay} n={3} className="text-[10px] leading-relaxed" style={{ breakBefore: "page" }}>
        <h2 className="font-heading font-bold text-sm mb-2">{T("p3_title")}</h2>
        <p className="font-mono-data mb-3">{T("p3_vin_line")}</p>
        {p3Mode === "legacy" ? (
          <>
            <p className="font-bold">{T("p3_odometer_title")}</p>
            <p>{T("p3_odometer_body")}</p>
            <p className="font-bold mt-3">{T("p3_prioruse_title")}</p>
            <p>{T("p3_prioruse_body")}</p>
            <p className="font-bold mt-3">{T("p3_priordamage_title")}</p>
            <p>{T("p3_priordamage_body")}</p>
          </>
        ) : (
          <>
            <Page3Checks T={T} checks={checks} editable={editable} onToggle={onToggleCheck} />
            <div className="mt-3" data-testid="p3-details-block">
              <p className="text-[8.5px] uppercase tracking-wide text-zinc-500 mb-1">{T("p3_details_label")}</p>
              <EditableNoteBox
                value={d.bos_details || ""}
                editable={editable}
                onCommit={onCommitDetails}
                maxLength={DETAILS_MAX}
                heightClass="h-[1in]"
                placeholder="Details behind any ticked box…"
                testid="p3-details"
              />
            </div>
          </>
        )}
        <div className="h-16 mt-4" />
      </BosPage>

      {/* ============ PAGE 4 ============ */}
      <BosPage only={only} overlay={overlay} n={4} className="text-[9px] leading-relaxed" style={{ breakBefore: "page" }}>
        <h2 className="font-heading font-bold text-sm mb-2">{T("p4_title")}</h2>
        <div className="space-y-1.5">
          {Array.from({ length: 14 }, (_, i) => i + 1).map((n) => (
            <p key={n}><b>{T(`term_${n}_title`)}</b> {T(`term_${n}_body`)}</p>
          ))}
        </div>
        <div className="h-20 mt-8" />
      </BosPage>
    </>
  );
}

function SectionTitle({ children }) {
  return <h3 className="font-bold uppercase text-[10px] tracking-wide bg-zinc-800 text-white px-2 py-0.5 mt-2 mb-1">{children}</h3>;
}

function Field({ label, value }) {
  return (
    <div className="flex gap-1 items-baseline">
      <span className="text-[8.5px] uppercase text-zinc-500 shrink-0">{label}:</span>
      <span className="font-medium border-b border-zinc-300 flex-1 min-h-[12px]">{value}</span>
    </div>
  );
}

function PriceLine({ l, v, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold" : ""}`}>
      <span className="font-sans">{l}</span>
      <span>{v}</span>
    </div>
  );
}

function Disclosure({ title, children }) {
  return (
    <p><span className="font-bold">{title}</span> {children}</p>
  );
}

/** One page-3 disclosure line. A live button when editable (keyboard-reachable,
 * focus ring, accessible name = label); a static ☑/☐ line when read-only or
 * printed. */
function CheckRow({ checked, editable, onToggle, label, testid }) {
  const box = checked ? "☑" : "☐";
  if (!editable) {
    return (
      <div className="flex items-start gap-1.5" data-testid={testid} data-checked={checked ? "1" : "0"}>
        <span className="text-[12px] leading-none" aria-hidden="true">{box}</span>
        <span>{label}</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={onToggle}
      data-testid={testid}
      data-checked={checked ? "1" : "0"}
      className="flex items-start gap-1.5 text-left rounded-sm px-0.5 -mx-0.5 cursor-pointer transition-colors hover:bg-black/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
    >
      <span className="text-[12px] leading-none" aria-hidden="true">{box}</span>
      <span>{label}</span>
    </button>
  );
}

/** Page-1 service-plan answer. Read-only / print: the legacy static line
 * (☑ YES ☐ NO / ☐ YES ☑ NO / YES / NO). Editable: a YES/NO radio pair — picking
 * one clears the other, re-clicking the selected one clears back to blank so
 * "not answered" stays possible (TICKET-DEAL-001 follow-up). */
function ServicePlanChoice({ prompt, value, editable, onSet }) {
  if (!editable) {
    const line = value === "Yes" ? "☑ YES   ☐ NO" : value === "No" ? "☐ YES   ☑ NO" : "YES / NO";
    return (
      <p className="text-[10px]" data-testid="service-plan-line">
        {prompt}&nbsp; <b data-testid="service-plan-static">{line}</b>
      </p>
    );
  }
  const Opt = ({ val, label }) => {
    const on = value === val;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        aria-label={`${prompt} — ${label}`}
        onClick={() => onSet(on ? "" : val)}
        data-testid={`service-plan-${val.toLowerCase()}`}
        data-checked={on ? "1" : "0"}
        className="inline-flex items-center gap-1 rounded-sm px-1 -mx-0.5 cursor-pointer transition-colors hover:bg-black/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1"
      >
        <span className="text-[12px] leading-none" aria-hidden="true">{on ? "☑" : "☐"}</span>
        <b>{label}</b>
      </button>
    );
  };
  return (
    <p className="text-[10px]" role="radiogroup" aria-label={prompt} data-testid="service-plan-choice">
      {prompt}&nbsp;
      <span className="inline-flex items-center gap-3 align-middle">
        <Opt val="Yes" label="YES" />
        <Opt val="No" label="NO" />
      </span>
    </p>
  );
}

/** Inline single-line F&I Manager name field (screen only — its parent is
 * no-print). Blank / the dealer default commits an empty override so a later
 * Dealer Info change still flows through on an unsigned deal. */
function FiNameInput({ value, dealerDefault, onCommit }) {
  const [val, setVal] = useState(value || "");
  const [status, setStatus] = useState("");
  useEffect(() => { setVal(value || ""); }, [value]);
  const commit = async () => {
    const next = (val.trim() === "" || val === dealerDefault) ? "" : val;
    const currentOverride = value && value !== dealerDefault ? value : "";
    if (next === currentOverride) return;
    setStatus("saving");
    try {
      await onCommit(next);
      setStatus("saved");
      setTimeout(() => setStatus((s) => (s === "saved" ? "" : s)), 1400);
    } catch {
      setStatus("");
    }
  };
  return (
    <span className="inline-flex items-center">
      <input
        value={val}
        maxLength={40}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        placeholder={dealerDefault}
        aria-label="F&I Manager name"
        data-testid="fi-name-input"
        className="w-44 bg-transparent border-b border-dashed border-zinc-400 focus:outline-none focus:border-blue-600 text-[10px] px-0.5"
      />
      {status === "saved" && <span className="text-emerald-600 ml-1 text-[9px]" data-testid="fi-name-saved">✓</span>}
    </span>
  );
}

/** Page-1 header F&I Manager line. Read-only / print: plain "F&I Manager: {name}"
 * or nothing when hidden. Editable: a no-print inline name field + show/hide
 * toggle, plus a print-only plain-text twin so a printed unsigned deal still
 * shows the line cleanly (TICKET-DEAL-001 follow-up). */
function FiManagerLine({ editable, show, name, dealerDefault, onToggle, onCommitName }) {
  if (!editable) {
    if (!show) return <span data-testid="fi-line-hidden" />;
    return <span data-testid="fi-line">F&amp;I Manager: {name}</span>;
  }
  return (
    <span data-testid="fi-line-editable">
      {show ? (
        <span className="no-print inline-flex items-center gap-1">
          F&amp;I Manager:&nbsp;
          <FiNameInput value={name} dealerDefault={dealerDefault} onCommit={onCommitName} />
          <button
            type="button"
            onClick={onToggle}
            aria-label="Hide the F&I Manager line"
            title="Hide the F&I Manager line"
            data-testid="fi-hide-button"
            className="ml-1 text-zinc-400 hover:text-zinc-700 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
          >
            <EyeOff className="h-3.5 w-3.5" />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          aria-label="Show the F&I Manager line"
          title="Show the F&I Manager line"
          data-testid="fi-show-button"
          className="no-print inline-flex items-center gap-1 italic text-zinc-400 hover:text-zinc-700 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
        >
          <Eye className="h-3.5 w-3.5" /> F&amp;I Manager (hidden)
        </button>
      )}
      {show && <span className="print-only" data-testid="fi-line-print">F&amp;I Manager: {name}</span>}
    </span>
  );
}

/** The three page-3 disclosure groups (Odometer / Prior use / Prior damage). */
function Page3Checks({ T, checks, editable, onToggle }) {
  return (
    <>
      {P3_CHECK_GROUPS.map((g) => (
        <div key={g.group} className="mt-3" data-testid={`p3-group-${g.group}`}>
          <p className="font-bold">{T(g.titleKey)}</p>
          {g.introKey && <p className="font-mono-data text-[9.5px] mt-0.5">{T(g.introKey)}</p>}
          <div className={g.group === "prioruse" ? "flex flex-wrap gap-x-6 gap-y-1 mt-1" : "space-y-1 mt-1"}>
            {g.items.map((it) => (
              <CheckRow
                key={it.id}
                checked={!!checks?.[it.id]}
                editable={editable}
                onToggle={() => onToggle && onToggle(g.group, it.id)}
                label={T(it.key)}
                testid={`p3-check-${it.id}`}
              />
            ))}
          </div>
          {g.footerKey && <p className="mt-1.5 text-[9.5px]">{T(g.footerKey)}</p>}
        </div>
      ))}
    </>
  );
}

/** Fixed-size note box. A textarea (with char counter + Saved indicator) when
 * editable; a static bordered block when read-only or printed. Never grows, so
 * it can never push content under a signature spot (TICKET-DEAL-001). */
function EditableNoteBox({ value, editable, onCommit, maxLength, heightClass, placeholder, testid }) {
  const [val, setVal] = useState(value || "");
  const [status, setStatus] = useState(""); // "" | "saving" | "saved"
  useEffect(() => { setVal(value || ""); }, [value]);

  if (!editable) {
    return (
      <div
        className={`w-full border border-zinc-300 p-1.5 text-[10px] whitespace-pre-wrap break-words overflow-hidden ${heightClass}`}
        data-testid={`${testid}-readonly`}
      >
        {value || ""}
      </div>
    );
  }

  const commit = async () => {
    if ((val || "") === (value || "")) return;
    setStatus("saving");
    try {
      await onCommit(val);
      setStatus("saved");
      setTimeout(() => setStatus((s) => (s === "saved" ? "" : s)), 1600);
    } catch {
      setStatus("");
    }
  };

  return (
    <div className="relative">
      <textarea
        value={val}
        maxLength={maxLength}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        placeholder={placeholder}
        className={`w-full border border-zinc-300 p-1.5 text-[10px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-600 ${heightClass}`}
        data-testid={`${testid}-input`}
      />
      <div className="absolute right-1.5 bottom-1 flex items-center gap-2 no-print text-[8px] text-zinc-400 leading-none">
        <span data-testid={`${testid}-count`}>{val.length}/{maxLength}</span>
        {status === "saving" && <span data-testid={`${testid}-saving`}>Saving…</span>}
        {status === "saved" && <span className="text-emerald-600 font-semibold" data-testid={`${testid}-saved`}>Saved ✓</span>}
      </div>
    </div>
  );
}

/** Placeholder sample deal used to preview the document in the Settings editor. */
export const SAMPLE_DEAL = {
  bill_number: "SAMPLE-0001",
  vehicle_stock: "STK-000",
  customer_name: "Sample Buyer",
  customer_address: "100 Example Ave",
  customer_city: "Pickering",
  customer_province: "ON",
  customer_postal: "L1V 2P8",
  customer_phone: "(000) 000-0000",
  customer_email: "buyer@example.com",
  customer_license: "S0000-00000-00000",
  deal_date: "2026-01-01",
  insurance_company: "Sample Insurance",
  insurance_policy: "POL-000",
  insurance_exp: "2027-01-01",
  vehicle_year: 2022,
  vehicle_type: "SUV",
  vehicle_make: "Sample",
  vehicle_model: "Model X",
  vehicle_trim: "Base",
  vehicle_pkg: "—",
  vehicle_color: "Black",
  vehicle_int_color: "Black",
  vehicle_vin: "1SAMPLEVIN0000000",
  vehicle_odometer: 42000,
  sale_price: 25000,
  admin_fee: 499,
  safety_cert_fee: 129,
  warranty_name: "Powertrain",
  warranty_price: 1499,
  other_fees: [],
  service_plan: "Yes",
  notes: "Sample deal used only to lay out signing spots.",
  spot_signatures: {},
  bos_checks: {},
  bos_details: "",
  totals: {
    taxable_net: 27127,
    tax_exempt: false,
    hst: 3526.51,
    non_taxable_fees: 0,
    licensing_fee: 0,
    total: 30653.51,
    deposit: 1000,
    balance_due: 29653.51,
    trade_in_value: 0,
  },
};
